import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { API, formatApiError } from "../lib/api";
import { geocodeAddress, parseLatLng, reverseGeocode } from "../lib/geocode";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, PencilSimple, Trash, MapPin, Clock, Upload, Storefront, CalendarBlank, Lightning, GoogleLogo, CaretDown, CaretRight, Prohibit, ChartLine, Images, MagicWand, Megaphone, Toolbox } from "@phosphor-icons/react";
import MultiTruckSchedule from "../components/MultiTruckSchedule";
import ScheduleCalendarSyncNotice from "../components/dashboard/ScheduleCalendarSyncNotice";
import { fmtET } from "../lib/etTime";
import OwnerInsights from "../components/OwnerInsights";
import { ExpandablePinPicker } from "../components/FullscreenPinPicker";
import AnalyticsCard from "../components/AnalyticsCard";
import EventInquiriesTile from "../components/EventInquiriesTile";
import ViewersCard from "../components/ViewersCard";
import ProInsightsCard from "../components/ProInsightsCard";
import BoostCard from "../components/BoostCard";
import GoogleCalendarCard from "../components/dashboard/GoogleCalendarCard";
import FacebookCard from "../components/dashboard/FacebookCard";
import AppleCalendarCard from "../components/dashboard/AppleCalendarCard";
import OutlookCalendarCard from "../components/dashboard/OutlookCalendarCard";
import CalendarHowToDrawer from "../components/dashboard/CalendarHowToDrawer";
import SyncCalendarNudgeCard from "../components/dashboard/SyncCalendarNudgeCard";
import AbandonedCalendarBanner from "../components/dashboard/AbandonedCalendarBanner";
import FoundingProBadge from "../components/dashboard/FoundingProBadge";
import GettingStartedChecklist from "../components/dashboard/GettingStartedChecklist";
import FoundingCityBadge from "../components/FoundingCityBadge";
import ManageBillingButton from "../components/ManageBillingButton";
import ProUpgradeTourLauncher from "../components/ProUpgradeTourLauncher";
import { ProChallengeCard } from "../components/ProChallengeCard";
import FollowerCountCard from "../components/FollowerCountCard";
import NotifyReachCard from "../components/NotifyReachCard";
import ProfileCompletenessCard from "../components/ProfileCompletenessCard";
import EmbedScheduleCard from "../components/dashboard/EmbedScheduleCard";
import ProfileOnboardingTourLauncher from "../components/ProfileOnboardingTourLauncher";
import ProfileEditorEntryCard from "../components/ProfileEditorEntryCard";
import DashboardSection from "../components/dashboard/DashboardSection";
import HomeBaseNudge from "../components/HomeBaseNudge";

function todayIso(hour, minute) {
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

function emptyForm() {
  return {
    id: null,
    event_name: "",
    truck_name: "",
    address: "",
    lat: null,
    lng: null,
    pin_source: null,
    starts_time: "11:00",
    ends_time: "14:00",
    menu_text: "",
    menu_image_path: null,
    // Event cancellation (Feb 2026). The toggle is OFF by default for
    // every event — owners only flip it when something actually goes
    // wrong (weather, mechanical, etc.). When canceled, the pin stays
    // on the map painted red until ends_at instead of disappearing,
    // so customers know the truck didn't simply forget to post.
    is_canceled: false,
    cancel_reason: null,
    cancel_reason_custom: "",
  };
}

// Cancellation reason options — kept in sync with backend CANCEL_REASONS.
// Order matches the spec the owner reads top-to-bottom.
const CANCEL_REASONS = [
  { value: "weather", label: "Weather" },
  { value: "mechanical", label: "Mechanical Issue" },
  { value: "staffing", label: "Staffing Shortage" },
  { value: "organizer", label: "Event Organizer Canceled" },
  { value: "personal", label: "Personal Emergency" },
  { value: "other", label: "Other" },
];

function formatTime(iso) {
  return fmtET(iso, { hour: "numeric", minute: "2-digit" }) || iso;
}

function formatDate(iso) {
  // Short day-of-week + numeric month/day in ET, e.g. "Wed · Aug 12".
  // Year is omitted unless the event isn't in the current calendar year.
  const evYear = fmtET(iso, { year: "numeric" });
  if (!evYear) return iso;
  const nowYear = String(new Date().getFullYear());
  const opts = { weekday: "short", month: "short", day: "numeric" };
  if (evYear !== nowYear) opts.year = "numeric";
  return fmtET(iso, opts);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Welcome toast for newly-paid Pros — mirrors the same one-shot flag
  // consumed by the profile editor. Fires here when the owner hits
  // "Go to dashboard" from `/billing/success` instead of the primary
  // "Customize your public profile" CTA.
  useEffect(() => {
    try {
      const flag = sessionStorage.getItem("tt_just_upgraded_pro");
      if (flag === "1") {
        sessionStorage.removeItem("tt_just_upgraded_pro");
        toast.success("You're on Pro — welcome!", {
          description: "Everything is unlocked. Head to Profile to upload your logo and photos.",
          duration: 6000,
        });
      }
    } catch {
      /* private-mode Safari — non-fatal */
    }
  }, []);
  // Read-only — drives the initial Tabs selection so deep-links from
  // `/calendar` (e.g. `/dashboard?tab=schedule`) land owners on the
  // right edit surface. We don't write back to it on tab change since
  // the user's manual tab clicks shouldn't pollute the URL.
  const [searchParams] = useSearchParams();
  // Controlled tab state so quick-action cards can jump straight to a tab.
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return t === "schedule" || t === "insights" ? t : "today";
  });
  // Calendar connection status for the collapsed section header (Pro).
  const [calStatus, setCalStatus] = useState(null);
  // Profile completeness drives the Profile & tools default-open state.
  const [profileDone, setProfileDone] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [open, setOpen] = useState(false);
  // Cancel-only mini dialog state. Lets the owner toggle is_canceled on
  // ANY event (including GCal-synced ones, whose full edit dialog is
  // hidden because non-cancel fields would get overwritten on the next
  // sync). `cancelTarget` is the post being acted on; null means
  // dialog closed.
  const [cancelTarget, setCancelTarget] = useState(null);

  useEffect(() => {
    if (!user?.is_pro) return;
    (async () => {
      const [g, o] = await Promise.allSettled([
        api.get("/integrations/google/status"),
        api.get("/integrations/outlook/status"),
      ]);
      setCalStatus({
        google: g.status === "fulfilled" && !!g.value.data?.enabled,
        outlook: o.status === "fulfilled" && !!o.value.data?.enabled,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_pro]);

  useEffect(() => {
    api.get("/owner/profile-completeness")
      .then((r) => { setProfileDone(!!r.data?.is_complete); setCompleteness(r.data || null); })
      .catch(() => setProfileDone(null));
  }, []);

  const [cancelForm, setCancelForm] = useState({
    is_canceled: false,
    cancel_reason: null,
    cancel_reason_custom: "",
  });
  const [cancelSaving, setCancelSaving] = useState(false);

  const openCancel = (p) => {
    setCancelTarget(p);
    setCancelForm({
      is_canceled: !!p.is_canceled,
      cancel_reason: p.cancel_reason || null,
      cancel_reason_custom: p.cancel_reason_custom || "",
    });
  };

  const submitCancel = async () => {
    if (!cancelTarget) return;
    setCancelSaving(true);
    try {
      const payload = {
        is_canceled: !!cancelForm.is_canceled,
        cancel_reason: cancelForm.is_canceled ? (cancelForm.cancel_reason || null) : null,
        cancel_reason_custom: (cancelForm.is_canceled && cancelForm.cancel_reason === "other")
          ? (cancelForm.cancel_reason_custom || "").trim() || null
          : null,
      };
      const { data } = await api.put(`/trucks/${cancelTarget.id}`, payload);
      // Optimistically patch the post in our local list so the canceled
      // pill flips without a full refetch.
      setPosts((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      toast.success(payload.is_canceled ? "Event marked canceled" : "Event uncanceled");
      setCancelTarget(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    } finally {
      setCancelSaving(false);
    }
  };
  const [geocoding, setGeocoding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Google-Calendar-synced posts get folded into a collapsible
  // section so a single sync (which can drop in dozens of events at
  // once) doesn't drown out the owner's manual posts.
  const [gcalOpen, setGcalOpen] = useState(false);

  const loadMine = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trucks/mine");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "owner") loadMine();
  }, [user]);

  if (authLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: "/dashboard" }} />;
  if (user.role !== "owner")
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h2 className="font-display font-bold text-2xl">This area is for truck owners.</h2>
        <p className="text-muted-foreground mt-2">Sign up as an owner to post your daily spot.</p>
      </div>
    );

  const openNew = () => {
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (p) => {
    const s = new Date(p.starts_at);
    const e = new Date(p.ends_at);
    setForm({
      id: p.id,
      event_name: p.event_name || "",
      truck_name: p.truck_name,
      address: p.address,
      lat: p.lat,
      lng: p.lng,
      pin_source: null,
      starts_time: `${String(s.getHours()).padStart(2, "0")}:${String(s.getMinutes()).padStart(2, "0")}`,
      ends_time: `${String(e.getHours()).padStart(2, "0")}:${String(e.getMinutes()).padStart(2, "0")}`,
      menu_text: p.menu_text || "",
      menu_image_path: p.menu_image_path || null,
      is_canceled: !!p.is_canceled,
      cancel_reason: p.cancel_reason || null,
      cancel_reason_custom: p.cancel_reason_custom || "",
    });
    setOpen(true);
  };

  const onGeocode = async () => {
    if (!form.address) return;
    // Coordinates pasted instead of an address? (e.g. "40.0379, -76.3055"
    // copied from Google Maps.) Pin the EXACT spot the owner entered and
    // reverse-geocode a display address for the event card.
    const coords = parseLatLng(form.address);
    if (coords) {
      setGeocoding(true);
      const r = await reverseGeocode(coords.lat, coords.lng);
      setGeocoding(false);
      if (r?.address) {
        setForm((f) => ({
          ...f, address: r.address, lat: coords.lat, lng: coords.lng, pin_source: "coordinates",
        }));
        toast.success("Coordinates pinned — address filled in", {
          description: r.address,
          duration: 6000,
        });
      } else {
        setForm((f) => ({ ...f, lat: coords.lat, lng: coords.lng, pin_source: "coordinates" }));
        toast.message("Pinned your exact coordinates", {
          description:
            "No street address found for that spot — type a short description in the address box (e.g. \"Long's Park, Lancaster\"). The pin stays on your coordinates.",
          duration: 10000,
        });
      }
      return;
    }
    setGeocoding(true);
    const r = await geocodeAddress(form.address);
    setGeocoding(false);
    if (!r) {
      toast.error("Couldn't find that address — drop the pin manually below.");
      return;
    }
    // NEW (May 2026) — same strict gate as the schedule editor.
    // Require house number + street + ZIP, or fall through to the
    // manual fullscreen pin drop. Clearing lat/lng on a rejected
    // geocode forces the submit handler's "must confirm address"
    // check to fire instead of silently posting a centroid pin.
    if (r.is_full_address === false) {
      const missing = (r.missing_address_parts || []).join(", ") || "a complete address";
      toast.error(
        `Address is missing ${missing}. Type the full address including ZIP — or drop the pin manually below.`,
        { duration: 7000 },
      );
      setForm((f) => ({ ...f, lat: null, lng: null }));
      return;
    }
    setForm((f) => ({ ...f, lat: r.lat, lng: r.lng }));
    if (r.approximate) {
      toast.success(`Pinned near ${r.display_name.split(",").slice(0, 2).join(",")}`, {
        description: "Exact house number wasn't in OpenStreetMap, so we pinned the closest match.",
      });
    } else {
      toast.success("Location found");
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const data = new FormData();
    data.append("file", file);
    try {
      const { data: res } = await api.post("/uploads/menu-image", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, menu_image_path: res.path }));
      toast.success("Menu photo uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.lat == null || form.lng == null) {
      toast.error("Please confirm the address on the map first.");
      return;
    }
    const [sh, sm] = form.starts_time.split(":").map(Number);
    const [eh, em] = form.ends_time.split(":").map(Number);
    const payload = {
      event_name: form.event_name,
      truck_name: form.truck_name,
      address: form.address,
      lat: form.lat,
      lng: form.lng,
      pin_source: form.pin_source || null,
      starts_at: todayIso(sh, sm),
      ends_at: todayIso(eh, em),
      menu_text: form.menu_text,
      menu_image_path: form.menu_image_path,
      // Cancellation — always send all three so the server can clear
      // stale state when the owner un-cancels an event.
      is_canceled: !!form.is_canceled,
      cancel_reason: form.is_canceled ? (form.cancel_reason || null) : null,
      cancel_reason_custom: (form.is_canceled && form.cancel_reason === "other")
        ? (form.cancel_reason_custom || "").trim() || null
        : null,
    };

    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/trucks/${form.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/trucks", payload);
        toast.success("You're on the map!");
      }
      setOpen(false);
      await loadMine();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/trucks/${id}`);
      toast.success("Deleted");
      await loadMine();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Renders a single post card. `fromGcal` styles the card subtly
  // differently (no Edit button, since the truth lives in Google
  // Calendar — editing in TruckTrackr would be lost on next sync).
  const renderPostCard = (p, opts = {}) => {
    const fromGcal = opts.fromGcal === true;
    return (
      <motion.div
        key={p.id}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-border bg-card p-5"
        data-testid={`post-card-${p.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {p.is_canceled && (
              <span
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] rounded-full px-2 py-0.5 font-bold bg-rose-100 text-rose-700 border border-rose-300 mb-1.5"
                data-testid={`post-canceled-pill-${p.id}`}
                title="Event canceled"
              >
                🚫 Canceled
              </span>
            )}
            {p.event_name && (
              <div className="font-display font-bold text-xl tracking-tight truncate">{p.event_name}</div>
            )}
            <div className={`${p.event_name ? "text-sm text-muted-foreground" : "font-display font-bold text-xl tracking-tight"}`}>
              {p.truck_name}
            </div>
            <div className="mt-1 text-sm text-muted-foreground flex items-start gap-1">
              <MapPin weight="duotone" className="h-4 w-4 mt-0.5 flex-none text-primary" />
              <span className="line-clamp-2">{p.address}</span>
            </div>
            <div className="mt-1 text-sm text-muted-foreground flex items-center gap-1">
              <CalendarBlank weight="duotone" className="h-4 w-4 text-primary" />
              {formatDate(p.starts_at)}
            </div>
            <div className="mt-1 text-sm text-muted-foreground flex items-center gap-1">
              <Clock weight="duotone" className="h-4 w-4 text-primary" />
              {formatTime(p.starts_at)} — {formatTime(p.ends_at)}
            </div>
          </div>
          <div className="flex gap-1 flex-wrap justify-end">
            {/* Cancel/Uncancel — shown for ALL events (daily-post
                AND GCal-synced). The cancel fields aren't in the GCal
                sync payload so re-syncs won't clobber the cancellation;
                this gives GCal owners a way to mark weather/mechanical
                cancellations without exposing fields Google overwrites.
                Labeled (not icon-only) so it's actually findable on
                mobile — a row of three identical icons is invisible. */}
            <Button
              size="sm"
              variant={p.is_canceled ? "default" : "outline"}
              onClick={() => openCancel(p)}
              data-testid={`cancel-post-${p.id}`}
              aria-label={p.is_canceled ? "Uncancel event" : "Cancel event"}
              className={
                "h-8 px-2.5 gap-1 text-xs " +
                (p.is_canceled
                  ? "bg-rose-600 hover:bg-rose-700 text-white border-rose-600"
                  : "border-rose-300 text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30")
              }
            >
              <Prohibit weight={p.is_canceled ? "fill" : "duotone"} className="h-3.5 w-3.5" />
              {p.is_canceled ? "Uncancel" : "Cancel"}
            </Button>
            {!fromGcal && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => openEdit(p)}
                data-testid={`edit-post-${p.id}`}
                aria-label="Edit"
              >
                <PencilSimple weight="duotone" className="h-4 w-4" />
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onDelete(p.id)}
              data-testid={`delete-post-${p.id}`}
              aria-label="Delete"
            >
              <Trash weight="duotone" className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
        {p.menu_text && (
          <p className="mt-3 text-sm line-clamp-3 whitespace-pre-wrap">{p.menu_text}</p>
        )}
        {p.menu_image_path && (
          <img
            src={`${API}/files/${p.menu_image_path}`}
            alt="Menu"
            className="mt-3 rounded-xl w-full h-40 object-cover"
          />
        )}
      </motion.div>
    );
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8" data-testid="owner-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight flex items-center gap-3 flex-wrap">
            {user.is_pro ? (
              <span data-testid="dashboard-pro-banner">
                TruckTrackr <span className="text-primary">Pro</span>
              </span>
            ) : (
              "Your truck"
            )}
            {user.is_pro && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary"
                data-testid="dashboard-pro-chip"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                Pro
              </span>
            )}
          </h1>
          {(user.founding_pro_cities || []).length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="dashboard-founding-city-badges">
              {user.founding_pro_cities.map((c) => (
                <FoundingCityBadge
                  key={c.city_slug}
                  citySlug={c.city_slug}
                  slotNumber={c.slot_number}
                  grantedAt={c.granted_at}
                />
              ))}
              <span className="text-[11px] font-semibold text-muted-foreground" data-testid="dashboard-founding-pro-life-note">
                {user.founding_pro_cities[0]?.expires_at
                  ? `Free Pro through ${new Date(user.founding_pro_cities[0].expires_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`
                  : "Free Pro for life"}
              </span>
            </div>
          ) : user.is_founding_pro ? (
            <div className="mt-2">
              <FoundingProBadge />
            </div>
          ) : null}
          <p className="text-sm text-muted-foreground mt-1">
            Welcome{user.name ? `, ${user.name}` : ""}. Post today&apos;s spot or set your schedule for this week.
          </p>
        </div>
        {user.is_pro || user.subscription_status ? (
          <div className="flex-none">
            {/* Stripe customer portal — update card, invoices, plan */}
            <ManageBillingButton />
          </div>
        ) : null}
      </div>

      <GettingStartedChecklist completeness={completeness} postsCount={posts.length} />

      {/* Payment-failed banner (Mar 2026). The Stripe webhook sets
          subscription_status="past_due" (and drops is_pro) on
          invoice.payment_failed; a successful retry flows back through
          customer.subscription.updated and restores Pro — so this
          banner self-dismisses once the card is fixed. */}
      {["past_due", "unpaid", "incomplete"].includes(user.subscription_status) && (
        <div
          className="mt-4 rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 flex items-center justify-between gap-3 flex-wrap"
          data-testid="payment-failed-banner"
        >
          <div className="min-w-0">
            <div className="text-sm font-bold text-rose-900">
              ⚠️ Your last Pro payment didn&rsquo;t go through
            </div>
            <div className="text-xs text-rose-800/90 mt-0.5">
              Usually an expired card. Update it below and your Pro benefits
              restore automatically — no need to re-subscribe.
            </div>
          </div>
          <ManageBillingButton variant="default" className="flex-none bg-rose-600 hover:bg-rose-700 text-white border-0" />
        </div>
      )}

      <AbandonedCalendarBanner />

      {/* ──────────────────────────────────────────────────────────────
          DAILY OPS — what owners come here for first.
          Schedule + Calendar Sync are above the fold. Analytics and
          growth signals live below.
          `defaultValue` reads a `?tab=schedule|today` query param so
          deep-links from the Calendar page (`/calendar` event cards)
          land owners on the right edit surface without a manual tap.
          ────────────────────────────────────────────────────────────── */}
      <HomeBaseNudge />

      <Tabs
        value={tab}
        onValueChange={setTab}
        className="mt-6"
        id="dashboard-daily-ops"
        data-testid="dashboard-daily-ops"
      >
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="today" className="gap-2" data-testid="tab-today">
            <Lightning weight="duotone" className="h-4 w-4" /> Today&apos;s post
          </TabsTrigger>
          <TabsTrigger value="schedule" className="gap-2" data-testid="tab-schedule">
            <CalendarBlank weight="duotone" className="h-4 w-4" /> Schedule this week
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2" data-testid="tab-insights">
            <ChartLine weight="duotone" className="h-4 w-4" /> Insights
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="mt-6">
          <div className="flex items-center justify-between gap-4 mb-6">
            <p className="text-sm text-muted-foreground">
              One-off posts override your scheduled events for that day.
            </p>
            <Button
              onClick={openNew}
              className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-11 px-5"
              data-testid="new-post-btn"
            >
              <Plus weight="bold" className="h-4 w-4" /> New post
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {loading ? (
              <div className="text-muted-foreground">Loading…</div>
            ) : posts.length === 0 ? (
              <div className="sm:col-span-2 rounded-2xl border border-dashed border-border p-10 text-center">
                <Storefront weight="duotone" className="h-10 w-10 mx-auto text-primary" />
                <h3 className="mt-3 font-display font-semibold text-xl">No posts yet</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Create your first daily post — customers will see you instantly.
                </p>
                <Button
                  onClick={openNew}
                  className="mt-5 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                  data-testid="empty-new-post-btn"
                >
                  Create your first post
                </Button>
              </div>
            ) : (
              posts.filter((p) => p.source !== "google_calendar").map((p) => renderPostCard(p))
            )}
          </div>

          {/* Google-Calendar-synced posts get their own collapsible
              folder so dozens of synced events don't bury the owner's
              manual posts. Header shows the count and acts as the
              toggle. */}
          {!loading && posts.some((p) => p.source === "google_calendar") && (
            <section className="mt-6 rounded-2xl border border-border bg-card overflow-hidden" data-testid="gcal-posts-folder">
              <button
                type="button"
                onClick={() => setGcalOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-muted/40 transition-colors"
                aria-expanded={gcalOpen}
                data-testid="gcal-posts-folder-toggle"
              >
                {gcalOpen ? (
                  <CaretDown weight="bold" className="h-4 w-4 text-muted-foreground flex-none" />
                ) : (
                  <CaretRight weight="bold" className="h-4 w-4 text-muted-foreground flex-none" />
                )}
                <GoogleLogo weight="duotone" className="h-5 w-5 text-primary flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-base tracking-tight">
                    From Google Calendar
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {posts.filter((p) => p.source === "google_calendar").length} synced event
                    {posts.filter((p) => p.source === "google_calendar").length === 1 ? "" : "s"}
                    {" · "}edit in Google Calendar to change
                  </div>
                </div>
              </button>
              {gcalOpen && (
                <div className="px-5 pb-5 pt-1">
                  <div className="grid gap-4 sm:grid-cols-2">
                    {posts
                      .filter((p) => p.source === "google_calendar")
                      .slice()
                      .sort((a, b) => {
                        // Soonest first. Past-due events still bubble up
                        // first within the past block (they're rare —
                        // sync removes events that finished hours ago,
                        // but a freshly-ended event could land here).
                        const sa = new Date(a.starts_at || 0).getTime();
                        const sb = new Date(b.starts_at || 0).getTime();
                        return sa - sb;
                      })
                      .map((p) => renderPostCard(p, { fromGcal: true }))}
                  </div>
                </div>
              )}
            </section>
          )}
        </TabsContent>

        <TabsContent value="schedule" className="mt-6">
          <div className="mb-4">
            <ScheduleCalendarSyncNotice />
          </div>
          <MultiTruckSchedule />
        </TabsContent>

        <TabsContent value="insights" className="mt-6">
          <OwnerInsights />
        </TabsContent>
      </Tabs>

      {/* Food Truck Resources — key owner tool, right under posting controls */}
      <div className="mt-6">
        <a
          href="/resources"
          data-testid="dashboard-resources-link"
          className="group block rounded-2xl border bg-card p-5 hover:border-primary/60 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center flex-none">
              <Toolbox weight="duotone" className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-base tracking-tight">Food Truck Resources</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Find trusted businesses that help keep your truck running.
              </p>
            </div>
            <span className="text-primary font-medium text-sm mt-1 group-hover:translate-x-0.5 transition-transform">→</span>
          </div>
        </a>
      </div>

      {/* ──────────────────────────────────────────────────────────────
          QUICK ACTIONS — Post Studio + AI schedule import, right under
          the daily-ops tabs (Aug 2026 dashboard declutter).
          ────────────────────────────────────────────────────────────── */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="dashboard-quick-actions">
        <a
          href="/dashboard/schedule-post"
          data-testid="dashboard-schedule-post-link"
          className="group rounded-2xl border bg-card p-5 hover:border-primary/60 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center flex-none">
              <Images weight="duotone" className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-base tracking-tight">Post Studio</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Turn your schedule into share-ready images on your own truck photo.
              </p>
            </div>
            <span className="text-primary font-medium text-sm mt-1 group-hover:translate-x-0.5 transition-transform">→</span>
          </div>
        </a>
        <button
          type="button"
          onClick={() => navigate("/dashboard/schedule-import")}
          data-testid="dashboard-import-image-btn"
          className="group rounded-2xl border bg-card p-5 text-left hover:border-primary/60 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center flex-none">
              <MagicWand weight="duotone" className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-base tracking-tight">
                Magic schedule import
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Paste your schedule or upload a flyer photo — AI drafts your week for review.
              </p>
            </div>
            <span className="text-primary font-medium text-sm mt-1 group-hover:translate-x-0.5 transition-transform">→</span>
          </div>
        </button>
      </div>

      {/* 4-Week Pro Challenge progress (renders only when enrolled) */}
      <ProChallengeCard />

      {/* Marketing Kit — Pro print & social material generator */}
      <div className="mt-4">
        <a
          href="/dashboard/marketing-kit"
          data-testid="dashboard-marketing-kit-link"
          className="group block rounded-2xl border bg-card p-5 hover:border-primary/60 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center flex-none">
              <Megaphone weight="duotone" className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-base tracking-tight">
                Marketing Kit
                {!user.is_pro && (
                  <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary rounded-full px-2 py-0.5">Pro</span>
                )}
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Print-ready flyers, counter signs, social graphics & QR stickers — auto-branded from your profile.
              </p>
            </div>
            <span className="text-primary font-medium text-sm mt-1 group-hover:translate-x-0.5 transition-transform">→</span>
          </div>
        </a>
      </div>

      {/* Facebook Page — connect, auto-post & schedule import */}
      <div className="mt-4">
        <FacebookCard />
      </div>

      {/* Event Inquiries inbox preview — time-sensitive leads stay
          prominent. Self-hides when there are zero inquiries. */}
      <div className="mt-6">
        <EventInquiriesTile />
      </div>

      {/* ──────────────────────────────────────────────────────────────
          COLLAPSIBLE SECTIONS (Aug 2026 declutter) — calendar sync,
          analytics, profile & tools each fold into one dropdown.
          ────────────────────────────────────────────────────────────── */}
      {user.is_pro && (
        <div id="calendar-sync">
          <DashboardSection
            id="calendar"
            title="Calendar sync"
            subtitle={calStatus
              ? [
                  calStatus.google ? "Google ✓" : "Google",
                  calStatus.outlook ? "Outlook ✓" : "Outlook",
                  "Apple feed",
                ].join(" · ")
              : "Google, Outlook & Apple"}
            icon={<CalendarBlank weight="duotone" className="h-5 w-5 text-primary flex-none" />}
            action={<CalendarHowToDrawer />}
          >
            <GoogleCalendarCard />
            <OutlookCalendarCard />
            <AppleCalendarCard />
            <Link
              to="/dashboard/schedule-import"
              className="block rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3 text-sm hover:bg-primary/10 transition-colors"
              data-testid="magic-import-link"
            >
              <span className="font-semibold text-primary">✨ Not a calendar person?</span>{" "}
              <span className="text-muted-foreground">
                Paste your schedule from Notes or snap a photo — we'll draft the stops for you.
              </span>
            </Link>
          </DashboardSection>
        </div>
      )}

      <DashboardSection
        id="analytics"
        title="Analytics"
        subtitle="Views, visitors, followers & reach"
        icon={<ChartLine weight="duotone" className="h-5 w-5 text-primary flex-none" />}
      >
        <AnalyticsCard />
        <ViewersCard />
        <FollowerCountCard />
        <NotifyReachCard />
        {user.is_pro && <ProInsightsCard />}
      </DashboardSection>

      <DashboardSection
        id="profile-tools"
        title="Profile & tools"
        subtitle={profileDone === false
          ? "Finish your profile to rank higher"
          : profileDone
            ? "Profile complete ✓"
            : "Profile, embed & boost"}
        defaultOpen={profileDone === false}
        icon={<Storefront weight="duotone" className="h-5 w-5 text-primary flex-none" />}
      >
        <ProfileEditorEntryCard />
        <ProfileCompletenessCard />
        <EmbedScheduleCard user={user} />
        {user.is_pro && <BoostCard />}
      </DashboardSection>

      {/* One-time guided tour for new owners — fires automatically the
          first time a published-but-incomplete profile shows up, and
          stays dismissed for life after they close it. Self-mounting
          so we don't conditionally render here. */}
      <ProfileOnboardingTourLauncher />

      <SyncCalendarNudgeCard />
      <ProUpgradeTourLauncher />

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center p-0 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <motion.form
              onSubmit={onSubmit}
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 24, stiffness: 260 }}
              className="w-full sm:max-w-lg bg-card border border-border rounded-t-3xl sm:rounded-2xl p-6 max-h-[92vh] overflow-y-auto"
              data-testid="post-form"
            >
              <h2 className="font-display font-bold text-2xl tracking-tight">
                {form.id ? "Edit post" : "New daily post"}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Takes about 20 seconds. Post lives for today only.
              </p>

              <div className="mt-5 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ename">Event name (optional)</Label>
                  <Input
                    id="ename"
                    value={form.event_name}
                    onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                    placeholder="e.g. Neighborhood Pop-Up, Raney Cellars"
                    data-testid="form-event-name"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="tname">Truck name</Label>
                  <Input
                    id="tname"
                    value={form.truck_name}
                    onChange={(e) => setForm({ ...form, truck_name: e.target.value })}
                    required
                    data-testid="form-truck-name"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="addr">Location address</Label>
                  <div className="flex gap-2">
                    <Input
                      id="addr"
                      value={form.address}
                      onChange={(e) =>
                        setForm((f) =>
                          f.pin_source === "coordinates"
                            ? { ...f, address: e.target.value }
                            : { ...f, address: e.target.value, lat: null, lng: null }
                        )
                      }
                      placeholder="123 Main St, Lancaster PA 17603 — or paste coordinates"
                      required
                      data-testid="form-address"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onGeocode}
                      disabled={geocoding || !form.address}
                      data-testid="form-geocode-btn"
                    >
                      {geocoding ? "Finding…" : "Find"}
                    </Button>
                  </div>
                  {form.lat != null && (
                    <p className="text-xs text-primary" data-testid="form-geocoded-indicator">
                      ✓ Location pinned ({form.lat.toFixed(4)}, {form.lng.toFixed(4)})
                      {form.pin_source === "coordinates" ? " · exact coordinates" : ""}
                    </p>
                  )}
                  {form.lat == null && (
                    <p className="text-xs text-muted-foreground" data-testid="form-coords-hint">
                      Address not being found? Paste coordinates from Google Maps instead
                      (press &amp; hold the spot in Google Maps, then copy the numbers like{" "}
                      <span className="font-mono">40.0379, -76.3055</span>) and tap Find.
                    </p>
                  )}
                  {/* Pin picker — always render when the user has typed
                      an address, so the fullscreen manual-drop escape
                      hatch is available even when the geocoder rejected
                      the address as incomplete. */}
                  {form.address && (
                    <div className="space-y-1 pt-1">
                      <p className="text-xs text-muted-foreground">
                        {form.lat != null
                          ? "Pin looks wrong? Tap the map to fine-tune on a bigger view."
                          : "Drop a pin to mark the exact spot — opens a fullscreen map."}
                      </p>
                      <ExpandablePinPicker
                        lat={form.lat}
                        lng={form.lng}
                        onChange={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))}
                        testIdPrefix="form-pin"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="starts">Opens at</Label>
                    <Input
                      id="starts"
                      type="time"
                      value={form.starts_time}
                      onChange={(e) => setForm({ ...form, starts_time: e.target.value })}
                      required
                      data-testid="form-starts-time"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ends">Closes at</Label>
                    <Input
                      id="ends"
                      type="time"
                      value={form.ends_time}
                      onChange={(e) => setForm({ ...form, ends_time: e.target.value })}
                      required
                      data-testid="form-ends-time"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="menu">Menu (optional)</Label>
                  <Textarea
                    id="menu"
                    rows={4}
                    value={form.menu_text}
                    onChange={(e) => setForm({ ...form, menu_text: e.target.value })}
                    placeholder="Today: carnitas tacos, elote, churros…"
                    data-testid="form-menu-text"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Menu photo (optional)</Label>
                  <div className="flex items-center gap-3">
                    <label className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onUpload(e.target.files?.[0])}
                        data-testid="form-menu-image-input"
                      />
                      <span
                        className={`flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm cursor-pointer hover:border-primary/60 transition ${
                          uploading ? "opacity-60" : ""
                        }`}
                      >
                        <Upload weight="duotone" className="h-4 w-4" />
                        {uploading ? "Uploading…" : form.menu_image_path ? "Replace photo" : "Upload photo"}
                      </span>
                    </label>
                  </div>
                  {form.menu_image_path && (
                    <img
                      src={`${API}/files/${form.menu_image_path}`}
                      alt="Menu preview"
                      className="mt-2 rounded-lg h-32 w-full object-cover"
                    />
                  )}
                </div>

                {/* Event Status / Cancellation section.
                    OFF by default for every event. When toggled ON, we
                    reveal the reason dropdown (and the optional custom
                    free-text field when "Other" is picked). The backend
                    stamps `canceled_at` on first save and the customer-
                    facing map paints the pin red until ends_at instead
                    of dropping it — so customers know the truck was
                    scheduled but today is canceled, not forgotten. */}
                <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-3"
                     data-testid="form-cancel-section">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold flex items-center gap-1.5">
                        Event Status
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Mark this event canceled to keep the pin visible
                        but painted red until its end time. Customers
                        see &ldquo;Canceled&rdquo; instead of an empty spot.
                      </p>
                    </div>
                    <label className="inline-flex items-center cursor-pointer select-none">
                      <span className="mr-2 text-xs font-medium">
                        Cancel Event
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={!!form.is_canceled}
                        onChange={(e) => setForm({
                          ...form,
                          is_canceled: e.target.checked,
                          // Clear reason when uncanceling so the next
                          // cancellation starts fresh.
                          cancel_reason: e.target.checked ? form.cancel_reason : null,
                          cancel_reason_custom: e.target.checked ? form.cancel_reason_custom : "",
                        })}
                        data-testid="form-cancel-toggle"
                      />
                      <span className="relative h-5 w-9 rounded-full bg-muted-foreground/30 peer-checked:bg-rose-500 transition-colors">
                        <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${form.is_canceled ? "translate-x-4" : ""}`} />
                      </span>
                    </label>
                  </div>

                  {form.is_canceled && (
                    <div className="space-y-2 pt-1">
                      <Label htmlFor="cancel-reason">
                        Cancellation Reason
                      </Label>
                      <select
                        id="cancel-reason"
                        value={form.cancel_reason || ""}
                        onChange={(e) => setForm({
                          ...form,
                          cancel_reason: e.target.value || null,
                          // Drop the custom text when switching away from "Other".
                          cancel_reason_custom: e.target.value === "other" ? form.cancel_reason_custom : "",
                        })}
                        required
                        data-testid="form-cancel-reason"
                        className="w-full h-10 px-3 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                      >
                        <option value="">Pick a reason…</option>
                        {CANCEL_REASONS.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>

                      {form.cancel_reason === "other" && (
                        <div className="space-y-1.5">
                          <Label htmlFor="cancel-reason-custom">
                            Custom Reason (optional)
                          </Label>
                          <Input
                            id="cancel-reason-custom"
                            type="text"
                            maxLength={120}
                            value={form.cancel_reason_custom}
                            onChange={(e) => setForm({
                              ...form,
                              cancel_reason_custom: e.target.value,
                            })}
                            placeholder="e.g. Equipment failure"
                            data-testid="form-cancel-reason-custom"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex gap-2 justify-end">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="form-cancel-btn">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                  data-testid="form-submit-btn"
                >
                  {saving ? "Saving…" : form.id ? "Save changes" : "Go live"}
                </Button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cancel-only mini dialog. Reachable from every event card via
          the 🚫 icon button — works for GCal-synced events too, because
          the cancel fields aren't part of the GCal sync overwrite set
          and therefore survive future re-syncs. */}
      <AnimatePresence>
        {cancelTarget && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => !cancelSaving && setCancelTarget(null)}
            data-testid="cancel-dialog-backdrop"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full sm:max-w-md bg-card rounded-t-2xl sm:rounded-2xl border border-border p-5 sm:p-6 shadow-xl"
              data-testid="cancel-dialog"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-xl font-bold tracking-tight">
                    {cancelForm.is_canceled ? "Uncancel event" : "Cancel event"}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    {cancelTarget.truck_name}
                    {cancelTarget.address ? ` · ${cancelTarget.address}` : ""}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 cursor-pointer select-none">
                  <span>
                    <div className="text-sm font-semibold">Cancel this event</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      The pin stays on the map painted red until end time.
                    </div>
                  </span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!cancelForm.is_canceled}
                    onChange={(e) => setCancelForm({
                      ...cancelForm,
                      is_canceled: e.target.checked,
                      cancel_reason: e.target.checked ? cancelForm.cancel_reason : null,
                      cancel_reason_custom: e.target.checked ? cancelForm.cancel_reason_custom : "",
                    })}
                    data-testid="cancel-dialog-toggle"
                  />
                  <span className="relative h-6 w-11 rounded-full bg-muted-foreground/30 peer-checked:bg-rose-500 transition-colors flex-none">
                    <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${cancelForm.is_canceled ? "translate-x-5" : ""}`} />
                  </span>
                </label>

                {cancelForm.is_canceled && (
                  <div className="space-y-2">
                    <Label htmlFor="cancel-dialog-reason">Cancellation Reason</Label>
                    <select
                      id="cancel-dialog-reason"
                      value={cancelForm.cancel_reason || ""}
                      onChange={(e) => setCancelForm({
                        ...cancelForm,
                        cancel_reason: e.target.value || null,
                        cancel_reason_custom: e.target.value === "other" ? cancelForm.cancel_reason_custom : "",
                      })}
                      data-testid="cancel-dialog-reason"
                      className="w-full h-10 px-3 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                    >
                      <option value="">Pick a reason…</option>
                      {CANCEL_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    {cancelForm.cancel_reason === "other" && (
                      <div className="space-y-1.5">
                        <Label htmlFor="cancel-dialog-custom">Custom reason (optional)</Label>
                        <Input
                          id="cancel-dialog-custom"
                          type="text"
                          maxLength={120}
                          value={cancelForm.cancel_reason_custom}
                          onChange={(e) => setCancelForm({ ...cancelForm, cancel_reason_custom: e.target.value })}
                          placeholder="e.g. Equipment failure"
                          data-testid="cancel-dialog-custom"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-5">
                <Button
                  variant="ghost"
                  onClick={() => setCancelTarget(null)}
                  disabled={cancelSaving}
                  data-testid="cancel-dialog-close"
                >
                  Close
                </Button>
                <Button
                  onClick={submitCancel}
                  disabled={cancelSaving || (cancelForm.is_canceled && !cancelForm.cancel_reason)}
                  data-testid="cancel-dialog-save"
                  className={cancelForm.is_canceled ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
                >
                  {cancelSaving ? "Saving…" : (cancelForm.is_canceled ? "Mark canceled" : "Uncancel event")}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
